import IconButton from '@mui/material/IconButton';
import React, { FC, useMemo } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';
import {
  LiveTrackingOrderIndicator,
  LiveTrackingTripIndicator
} from '../../TwLiveTrackingIndicator/TwLiveTrackingIndicator';
import useAuth from '../../../providers/Auth/Auth.provider';
import { USER_ROLE } from '../../../providers/User/User.model';

import TwEdiBadge from '../../TwEdiBadge/TwEdiBadge';
import { Equipment } from '../../../redux/api/equipment/equipment.model';
import CloseIcon from '@mui/icons-material/Close';
import { Trip, TRIP_STATUS, TripStatusColor } from '../../../redux/api/trips/trips.model';
import { ORDER_STATUS, OrderStatusColor } from '../../../redux/api/orders/orders.model';
import { EntityType } from '../../../redux/Global.model';
import TwBadge from '../../TwBadge/TwBadge';
import StringUtils from '../../../utils/stringUtils';

interface HeaderOverlayProps {
  entityType: EntityType; // New
  client: string;
  readableId: string | number;
  currentTab: number;
  truck?: Equipment;
  ediLoadTenderId?: string | null;
  loadStatus?: ORDER_STATUS;
  tripStatus?: TRIP_STATUS;
  displaySegments?: Trip[];
  onClose: () => void;
}

const HeaderOverlay: FC<HeaderOverlayProps> = ({
  entityType,
  client,
  readableId,
  currentTab,
  truck,
  ediLoadTenderId,
  loadStatus,
  tripStatus,
  displaySegments,
  onClose
}): JSX.Element => {
  const { data } = useAuth();
  const { enableLiveTracking } = useFeatureFlags();
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const userData = data.userData;
  const isDriver = userData?.role === USER_ROLE.DRIVER;
  const isCarrier = userData?.role === USER_ROLE.CARRIER;
  const isDriverOrCarrier = isDriver || isCarrier;

  const isEDISourced = !!ediLoadTenderId;
  const isLiveTrackingEnabledAndUserNonDriverCarrier =
    enableLiveTracking && !matchesMobileOrTablet && !isDriverOrCarrier;

  const isOrder = entityType === EntityType.ORDER;
  const isTrip = entityType === EntityType.TRIP;

  const getTitle = (): string => {
    if (isOrder) {
      return `${client} • Order #${readableId} `;
    }
    if (isTrip) {
      return `Trip #${readableId} `;
    }
    return '';
  };

  const statusContent = useMemo(() => {
    let content = {
      label: '',
      color: ''
    };
    if (isOrder && loadStatus) {
      content = {
        label: StringUtils.getOrderStatusLabel(loadStatus),
        color: OrderStatusColor[loadStatus]
      };
    }
    if (isTrip && tripStatus) {
      content = {
        label: StringUtils.getSegmentStatusLabel(tripStatus),
        color: TripStatusColor[tripStatus]
      };
    }

    return content;
  }, [isOrder, isTrip, loadStatus, tripStatus]);

  return (
    <Box component="header" className="load-detail__header">
      <Stack
        flexDirection={matchesMobileOrTablet ? 'column' : 'row'}
        gap={1}
        alignItems={matchesMobileOrTablet ? 'left' : 'center'}
        sx={{ width: '100%' }}
      >
        <Box component="div" className="load-detail__header--title">
          <Box
            component="div"
            display={'flex'}
            alignItems={'center'}
            justifyContent={'left'}
            width={'100%'}
          >
            {getTitle()}

            <Box display={'flex'} marginLeft={'8px'}>
              <TwBadge
                content={statusContent.label}
                sx={{
                  color: `#fff !important`,
                  backgroundColor: `${statusContent.color} !important`,
                  marginRight: '8px'
                }}
                className="tid-load-status"
              />
              {isEDISourced && <TwEdiBadge />}
            </Box>
          </Box>
          {isLiveTrackingEnabledAndUserNonDriverCarrier && currentTab === 0 && (
            <LiveTrackingOrderIndicator orderStatus={loadStatus} equipment={truck} />
          )}
          {isLiveTrackingEnabledAndUserNonDriverCarrier && currentTab > 0 && (
            <LiveTrackingTripIndicator
              tripStatus={displaySegments?.[currentTab - 1]?.status}
              equipment={truck}
            />
          )}
        </Box>
      </Stack>
      <IconButton onClick={onClose} size="small">
        <CloseIcon />
      </IconButton>
    </Box>
  );
};

export default HeaderOverlay;
